import logger from '@/shared/utils/core/logger';

/**
 * OAuth Configuration Helper
 * Utilities for diagnosing and fixing Google OAuth configuration issues
 */

/**
 * Get current environment details for OAuth configuration
 * @returns {Object} Environment details needed for OAuth setup
 */
export function getOAuthEnvironmentDetails() {
  const origin = window.location.origin;
  const hostname = window.location.hostname;
  const protocol = window.location.protocol;
  const port = window.location.port;

  return {
    origin,
    hostname,
    protocol,
    port: port || (protocol === 'https:' ? '443' : '80'),
    isLocalhost: hostname === 'localhost' || hostname === '127.0.0.1',
    isCodespaces: hostname.includes('.github.dev'),
    isProduction: !hostname.includes('localhost') && !hostname.includes('.github.dev'),
    authHandlerUri: `${origin}/__/auth/handler`,
    firebaseAuthDomain: 'clarity-app-334ee.firebaseapp.com',
    firebaseAuthHandlerUri: 'https://clarity-app-334ee.firebaseapp.com/__/auth/handler'
  };
}

/**
 * Generate configuration instructions for current environment
 * @returns {Object} Configuration instructions
 */
export function generateConfigInstructions() {
  const env = getOAuthEnvironmentDetails();

  return {
    googleCloudConsole: {
      url: 'https://console.cloud.google.com/apis/credentials',
      javascriptOrigins: [env.origin],
      redirectUris: [
        env.authHandlerUri,
        env.firebaseAuthHandlerUri
      ]
    },
    firebaseConsole: {
      url: 'https://console.firebase.google.com',
      authorizedDomains: [env.hostname]
    },
    environment: env
  };
}

/**
 * Copy all required URIs to clipboard for easy setup
 * @returns {Promise<string>} Formatted configuration text
 */
export async function copyConfigToClipboard() {
  const env = getOAuthEnvironmentDetails();

  const configText = `
GOOGLE CLOUD CONSOLE - OAuth 2.0 Credentials
=============================================

Authorized JavaScript Origins:
${env.origin}

Authorized Redirect URIs:
${env.authHandlerUri}
${env.firebaseAuthHandlerUri}

FIREBASE CONSOLE - Authorized Domains
=====================================
${env.hostname}
`.trim();

  try {
    await navigator.clipboard.writeText(configText);
    return configText;
  } catch (error) {
    logger.error('Failed to copy to clipboard:', error);
    throw error;
  }
}

/**
 * Log diagnostic information to console
 */
export function logOAuthDiagnostics() {
  const env = getOAuthEnvironmentDetails();
  const instructions = generateConfigInstructions();

  logger.debug(`
╔══════════════════════════════════════════════════════════════════╗
║                    OAUTH CONFIGURATION HELPER                    ║
╚══════════════════════════════════════════════════════════════════╝

Current Environment:
────────────────────
  Origin:     ${env.origin}
  Hostname:   ${env.hostname}
  Protocol:   ${env.protocol}
  Type:       ${env.isLocalhost ? 'Local Development' : env.isCodespaces ? 'GitHub Codespaces' : 'Production'}

Required Configuration:
───────────────────────

1. GOOGLE CLOUD CONSOLE (${instructions.googleCloudConsole.url})

   Authorized JavaScript Origins:
   └─ ${env.origin}

   Authorized Redirect URIs:
   ├─ ${env.authHandlerUri}
   └─ ${env.firebaseAuthHandlerUri}

2. FIREBASE CONSOLE (${instructions.firebaseConsole.url})

   Authorized Domains:
   └─ ${env.hostname}

Quick Copy Commands (run in browser console):
─────────────────────────────────────────────
  copy("${env.origin}")                    // JavaScript Origin
  copy("${env.authHandlerUri}")            // Redirect URI
  copy("${env.hostname}")                  // Firebase Domain

═══════════════════════════════════════════════════════════════════
`);
}

/**
 * Check if OAuth is likely configured correctly
 * @returns {Object} Configuration status
 */
export function checkOAuthStatus() {
  const env = getOAuthEnvironmentDetails();

  // We can't directly check if OAuth is configured,
  // but we can provide guidance based on environment
  return {
    environment: env,
    likelyIssues: [
      !env.isLocalhost && !env.isCodespaces && 'Production domain may need to be added to OAuth settings',
      env.isCodespaces && 'Codespaces URLs change - ensure current URL is added to OAuth settings',
      env.isLocalhost && 'Ensure localhost is in Firebase authorized domains'
    ].filter(Boolean),
    recommendations: [
      'Check browser console for detailed error messages',
      'Verify all URLs match exactly (no trailing slashes)',
      'Wait 5-10 minutes after making changes for propagation',
      'Try in an incognito window to avoid cache issues'
    ]
  };
}

// Expose helper to window for easy console access in development
if (import.meta.env.DEV) {
  window.oauthHelper = {
    getDetails: getOAuthEnvironmentDetails,
    getInstructions: generateConfigInstructions,
    copyConfig: copyConfigToClipboard,
    logDiagnostics: logOAuthDiagnostics,
    checkStatus: checkOAuthStatus
  };

  logger.debug('💡 OAuth helper available! Run: oauthHelper.logDiagnostics()');
}

export default {
  getOAuthEnvironmentDetails,
  generateConfigInstructions,
  copyConfigToClipboard,
  logOAuthDiagnostics,
  checkOAuthStatus
};
